import { useState } from "react";
import { IndianRupee } from "lucide-react";
import { cn } from "@/lib/utils";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useRazorpayConnected } from "../api/useRazorpayMutations";
import CollectPaymentDialog from "./CollectPaymentDialog";

/**
 * "Collect payment" for the lead open in the drawer. Renders nothing unless
 * Razorpay is connected. `variant`:
 *   - "footer": the labelled pill in the drawer's bottom bar (every tab);
 *   - "inline": a small link, e.g. under a program's "₹X pending".
 */
export default function CollectPaymentButton({
  variant = "footer",
  dealId = null,
  label,
  className,
}: {
  variant?: "footer" | "inline";
  dealId?: number | null;
  label?: string;
  className?: string;
}) {
  const connected = useRazorpayConnected();
  const { lead } = useActiveDataMutation();
  const [open, setOpen] = useState(false);
  const [openKey, setOpenKey] = useState(0);
  const leadId = Number(lead?.id);
  if (!connected || !Number.isFinite(leadId) || leadId <= 0) return null;

  const show = () => {
    setOpenKey((k) => k + 1);
    setOpen(true);
  };

  return (
    <>
      {variant === "footer" ? (
        <button
          type="button"
          onClick={show}
          title="Send a Razorpay payment link — recorded automatically when paid"
          className={cn(
            "inline-flex h-10 shrink-0 items-center gap-1.5 rounded-[12px] bg-[#e8f3ff] px-3.5 text-[13.5px] font-semibold text-[#1f6fd1] transition-colors hover:bg-[#d8eaff]",
            className,
          )}
        >
          <IndianRupee className="size-4" strokeWidth={2.4} />
          {label ?? "Collect payment"}
        </button>
      ) : (
        <button
          type="button"
          onClick={show}
          className={cn(
            "inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1f6fd1] hover:underline",
            className,
          )}
        >
          <IndianRupee className="size-3" strokeWidth={2.6} />
          {label ?? "Collect"}
        </button>
      )}
      {open && (
        <CollectPaymentDialog
          key={openKey}
          open={open}
          onOpenChange={setOpen}
          initialDealId={dealId}
          lead={{
            id: leadId,
            name: lead?.name ?? null,
            phone: lead?.phone ?? null,
            phoneCountryCode: lead?.phoneCountryCode ?? null,
            email: lead?.email ?? null,
          }}
        />
      )}
    </>
  );
}
